import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import Button from '../../atoms/button';
import clsx from 'clsx';
import PrimaryLoader from '../../atoms/primaryLoader/PrimaryLoader';
import { TextLink } from '../../atoms/textLink/TextLink';
import { FinalSaleBadge, FinalSaleDescription } from '../../atoms/finalSale';
import Price from '../../molecules/price/Price';
import Swatches from '../../molecules/swatches';
import { Text } from '../../typography/text/Text';
import styles from './ProductActions.module.scss';
import './ProductActions.global.scss';
import { QtySelector } from './QtySelector';
import { SizeSelector } from './SizeSelector';
import { getPrices, sortSiblings } from '@srcDS/utils/helpers';
import showLoginErrorMessage from '../../../utils/showLoginErrorMessage';
import { useOpenSignForm } from '@srcDS/utils/useOpenSignForm';
import FitslugComponent from './components/FitslugComponent';
import AuthMessageComponent from './components/AuthMessageComponent';
import HeaderAction from './components/HeaderAction';
import LinkToWishlist from '@srcDS/components/molecules/linkToWishlist/LinkToWishlist';
import { FORM_TYPE } from '../../molecules/account/accountDropdown/AccountDropdown';
import { optionsActions } from './options';
import { isEmptyArray } from '@srcDS/utils/assertions';
import ProductRating from '../../molecules/productRating/ProductRating';
import { ProductActionsProps } from '@srcDS/components/organisms/productActions/types';

// Constants
const customArgs = {
  label: '',
  variant: 'normal',
  labelPosition: 'normal',
};

const LOW_IMPACT = 'LOW IMPACT';

// Component
const ProductActions: React.FC<ProductActionsProps> = ({
  data,
  register,
  setValue,
  variant,
  variants,
  selectVariant,
  selectedQty,
  title,
  text,
  isLoggedIn,
  isSelectingSize,
  onAddToCart,
  addToRef,
  onChangeVariant,
  vipEnabled,
  statusMenu,
  setStatusMenu,
  setActiveOption,
  parentProduct,
  finalSale,
  finalSaleMessage,
  selectedStore,
  currentSelectedStore,
  findStoreType,
  storeList,
  hasAvailableSizeGuide,
  displayAlphaSize,
  productId,
}) => {
  const { signFormOpen } = useOpenSignForm();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [loading, setLoading] = useState(false);
  const isNewView = findStoreType === 'newFindAStore';

  const {
    projectSpecificData: { availableQuantity = 0, discount = {}, prices = [], name },
    attributes: { additionalsizeinfo, colour, modelinfo, fitslug, fitdescription, ...attr },
    images,
    id,
  } = variant;

  const dispatch = useDispatch();

  const {
    projectSpecificData: { siblings = [], sustainabilityslug },
  } = parentProduct;

  const { price, discountedPrice, onSale } = getPrices(prices);

  const defaultSiblingElement = {
    colour: colour || '',
    id: id,
    swatch: images.find((img) => img.label === 'swatch')?.url || images[0]?.url,
    url: parentProduct?._url,
  };

  const _aSiblings = siblings.constructor === Object ? Object.values(siblings) : siblings;

  const formattedSiblingsArray = !isEmptyArray(_aSiblings)
    ? [defaultSiblingElement, ...(_aSiblings as any[])]
    : [defaultSiblingElement];
  const sortedSiblings = sortSiblings(formattedSiblingsArray);
  const swatchData =
    sortedSiblings &&
    sortedSiblings.map((sibling, idx) => {
      if (!sibling) return null;

      return {
        id: `sibling-${parentProduct?.productId}-${idx}`,
        colorAttribute: sibling?.colour,
        imageUrl: sibling?.swatch,
        url: sibling?.url,
      };
    });

  const signUpForm = (formName = FORM_TYPE.LOGIN, isShowErrorMessage = true) => {
    signFormOpen(formName);
    if (isShowErrorMessage) {
      showLoginErrorMessage();
    }
  };

  const calculateQty = (): string | undefined => {
    if (isSelectingSize) return;

    if (variant && availableQuantity > 0) {
      if (availableQuantity > 0 && availableQuantity <= 10) {
        return `${availableQuantity} left in stock`;
      } else if (availableQuantity > 10 && availableQuantity <= 20) {
        return 'Style selling quick';
      }
    }
  };

  const setQty = (qty: number) => {
    if (qty <= 0) {
      return (
        <Text weight="light" size="xs">
          Out of stock
        </Text>
      );
    } else {
      if (qty > 0 && qty <= 10) {
        return <Text weight="light" size="xs">{`Only ${qty} left`}</Text>;
      }
    }
  };

  const renderTextLink = (className: string) => {
    return optionsActions.theme === 'Jag' ? (
      sustainabilityslug?.toUpperCase() === LOW_IMPACT ? (
        /*@ts-ignore*/
        <TextLink
          className={clsx(styles.link, className)}
          underline="visible"
          dataAttributes={{
            'data-lowimpact-button': 'lowimpact_button',
          }}
          onClick={(e) => {
            dispatch({ type: 'open-lowimpact' });
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          low impact
        </TextLink>
      ) : null
    ) : null;
  };

  const getStoreStatusForVariant = (): boolean => {
    const currentStore = storeList?.find((x) => x.id === currentSelectedStore?.id);
    return !!(currentStore?.availability && currentStore?.quantity > 0);
  };

  const hasVariantInStore = getStoreStatusForVariant();

  const priceSection = (
    <div className={styles.priceSection}>
      <div>
        <div className={styles.priceBlock}>
          {price && price !== 0 && (
            <Price
              className={styles.priceItem}
              placement="pdp"
              type="regular"
              onSale={!!discountedPrice && discountedPrice !== price}
              value={Number(price)}
            />
          )}
          {discountedPrice && discountedPrice !== price && (
            <Price
              className={styles.priceItem}
              placement="pdp"
              type={onSale && 'sale'}
              value={Number(discountedPrice)}
            />
          )}
          <ProductRating productId={productId} />
        </div>
      </div>
      {finalSale && (
        <FinalSaleBadge
          barrierPosition="left"
          className={{
            message: styles.finalSaleMessage,
            barrier: styles.finalSaleBarrier,
          }}
        />
      )}
      {renderTextLink(styles.linkDesktop)}
    </div>
  );

  return (
    <div className={clsx(styles.wrapper, { [styles.wrapperNew]: isNewView })}>
      <HeaderAction
        title={title}
        priceSection={priceSection}
        discount={discount}
        discountedPrice={discountedPrice}
        price={price}
        text={text}
        renderTextLink={renderTextLink}
        promoMessage={{
          name: data?.dmTitle,
          color: data?.dmColor,
          bgColor: data?.dmBackgroundColor,
          borderColor: data?.dmBorderColor,
        }}
      />
      {/*@ts-ignore*/}
      {sortedSiblings && <Swatches colorIndex={colour} swatchData={swatchData} className={styles.swatchesContainer} />}
      {optionsActions.theme === 'Sportscraft' && additionalsizeinfo && (
        <div className={styles.sizeCondition} dangerouslySetInnerHTML={{ __html: additionalsizeinfo }} />
      )}
      <div className={styles.tooltipContainer}>
        {((fitslug && !isNewView) || (isNewView && optionsActions.theme !== 'Sportscraft')) && (
          <FitslugComponent fitslug={fitslug} fitdescription={fitdescription} />
        )}
        {calculateQty() && (
          <Text className={styles.tooltipRightText} size="xs" as="p">
            {calculateQty()}
          </Text>
        )}
      </div>
      <div className={styles.selectorWrap}>
        {fitslug && isNewView && optionsActions.theme === 'Sportscraft' && (
          <div className={styles.tooltipContainer}>
            <FitslugComponent fitslug={fitslug} fitdescription={fitdescription} />
          </div>
        )}
        {isNewView && (
          <QtySelector
            label={
              optionsActions.theme === 'Sportscraft' ? (
                <span>
                  QUANTITY<b>:</b>
                </span>
              ) : null
            }
            customArgs={customArgs}
            register={register}
            setValue={setValue}
            availableQuantity={availableQuantity}
            variant={variant}
          />
        )}
        <SizeSelector
          isSelectingSize={isSelectingSize}
          variants={variants}
          variant={variant}
          register={register}
          statusMenu={statusMenu}
          setValue={setValue}
          onChangeVariant={onChangeVariant}
          selectVariant={selectVariant}
          customArgs={customArgs}
          setQty={setQty}
          setActiveOption={setActiveOption}
          displayAlphaSize={
            displayAlphaSize && variants?.slice(1, variants.length)?.some((x) => x?.attributes?.alphasize)
          }
          label={
            isNewView && optionsActions.theme === 'Sportscraft' ? (
              <span>
                SIZE<b>:</b>
              </span>
            ) : null
          }
        />
      </div>
      <div className={clsx(styles.sizeGuideBlock, modelinfo && styles.modelinfo)}>
        {modelinfo && (
          <Text size="xs" as="p" className={styles.textSizeDescription}>
            {modelinfo}
          </Text>
        )}
        {hasAvailableSizeGuide && (
          <span data-size-guide="size_guide_button">
            {/*@ts-ignore*/}
            <TextLink
              className={styles.sizeGuideLink}
              underline="visible"
              onClick={(e) => {
                dispatch({
                  type: 'open-sizeguide',
                  data: {
                    desc: attr?.['mm-description'],
                    productName: name,
                    price: (
                      <div className={clsx(styles.priceBlock, styles.priceBlockGuide)}>
                        {price && price !== 0 && (
                          <Price
                            className={styles.priceItem}
                            placement="pdp"
                            type="regular"
                            onSale={!!discountedPrice && discountedPrice !== price}
                            value={Number(price)}
                          />
                        )}
                        {discountedPrice && discountedPrice !== price && (
                          <Price
                            className={styles.priceItem}
                            placement="pdp"
                            type={onSale && 'sale'}
                            value={Number(discountedPrice)}
                          />
                        )}
                      </div>
                    ),
                    categoryIds: parentProduct?.categories,
                    imageUrl: variant?.images?.[0],
                    table: {
                      size: attr?.['mm-size'],
                      waist: attr?.['mm-waist'],
                      hip: attr?.['mm-hip'],
                      length: attr?.['mm-length'],
                      bust: attr?.['mm-bust'],
                    },
                  },
                });
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              Size Guide
            </TextLink>
          </span>
        )}
      </div>
      {finalSaleMessage && optionsActions.theme === 'Saba' && <FinalSaleDescription description={finalSaleMessage} />}
      <div
        className={styles.container}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {!isNewView && (
          <QtySelector
            customArgs={customArgs}
            register={register}
            setValue={setValue}
            availableQuantity={availableQuantity}
            variant={variant}
          />
        )}
        {/*@ts-ignore*/}
        <Button
          variant="primary"
          className={styles.btn}
          ref={addToRef}
          disableTouchEvent
          disabled={isAddingToCart}
          onClick={() => {
            if (isSelectingSize) {
              return setStatusMenu((prev) => ({
                status: true,
                label: !prev.label,
              }));
            }
            setIsAddingToCart(true);
            const selectedChannel = variant?.sku === selectedStore?.skuId ? selectedStore?.channel : null;
            return onAddToCart(variant, Number(selectedQty), selectedChannel)
              .then(() => setIsAddingToCart(false))
              .catch(() => setIsAddingToCart(false));
          }}
        >
          {isSelectingSize ? (
            'Select a size'
          ) : isAddingToCart ? (
            <PrimaryLoader mode="segment" theme="light" size={20} />
          ) : (
            'Add to bag'
          )}
        </Button>
        {/*@ts-ignore*/}
        <LinkToWishlist
          item={{
            productName: variant?.projectSpecificData?.name,
            lineItemId: variant?.projectSpecificData?.id,
            variant: variant,
          }}
          lineItemId={variant.lineItemId}
          firstSkuId={variant.sku}
          toSignUp={signUpForm}
          setLoading={setLoading}
          customClasses={{
            linkToWishlist: styles.iconButton,
          }}
          loading={loading}
          isIcon
        />
      </div>
      {finalSaleMessage && optionsActions.theme !== 'Saba' && <FinalSaleDescription description={finalSaleMessage} />}
      {isNewView && (
        <div className={styles.findAStoreButton}>
          {/*@ts-ignore*/}
          <Button
            data-new-find-store-action="find_store_action"
            variant="secondary"
            className={clsx({
              [styles.disabledButton]: currentSelectedStore && selectedStore?.channel?.id,
            })}
            onClick={(e) => {
              if (isSelectingSize) {
                return setStatusMenu((prev) => ({
                  status: true,
                  label: !prev.label,
                }));
              }
              dispatch({ type: 'open-storelocator' });
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            {currentSelectedStore && selectedStore?.channel?.id ? (
              <>
                <span
                  className={clsx(styles.dot, {
                    [styles.redDot]: !hasVariantInStore,
                  })}
                />
                {`Collect in ${currentSelectedStore.storeName} ${
                  optionsActions.theme === 'Sportscraft' ? '' : 'store'
                }`}
              </>
            ) : optionsActions.theme === 'Sportscraft' ? (
              'SEE STOCK AVAILABILITY'
            ) : (
              'FIND IN STORE'
            )}
          </Button>
          {currentSelectedStore && selectedStore?.channel?.id && (
            /*@ts-ignore*/
            <Button
              variant="invisible"
              as="text"
              disableTouchEvent
              data-new-find-store-change="find_store_change"
              className={styles.findAStoreButtonText}
              onClick={(e) => {
                if (isSelectingSize) {
                  return setStatusMenu((prev) => ({
                    status: true,
                    label: !prev.label,
                  }));
                }
                dispatch({ type: 'open-storelocator' });
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              Change Store
            </Button>
          )}
        </div>
      )}
      <AuthMessageComponent vipEnabled={vipEnabled} isLoggedIn={isLoggedIn} data={data} signUpForm={signUpForm} />
    </div>
  );
};

export default ProductActions;
